import { FormInstance } from 'antd'
import moment, { type Moment } from 'moment'

import type {
  EventScheduleForm,
  OutageForm,
  OutageMetadataBaseItem,
  RecurringScheduleForm,
  SelectInputOption,
} from './types'

export function initializeFormValues(supplyOutage: Gravitate.SupplyOutage) {
  const schedule = supplyOutage.schedule_local
  const scheduleType = schedule.schedule_type

  let schedule_local: RecurringScheduleForm | EventScheduleForm
  if (scheduleType === 'event') {
    schedule_local = {
      schedule_type: 'Event',
      start_date: moment(schedule.start_date),
      start_time: moment(schedule.start_date),
      end_selection: 'Custom',
      end_date: moment(schedule.end_date),
      end_time: moment(schedule.end_date),
      isForever: !schedule.end_date,
    }
  } else if (scheduleType === 'recurring') {
    const days = schedule.days.reduce(
      (acc, day) => ({
        ...acc,
        [day.day_of_week]: {
          enabled: true,
          start_time: moment(day.start_time, 'HH:mm'),
          end_time: moment(day.end_time, 'HH:mm'),
        },
      }),
      {}
    )

    schedule_local = {
      schedule_type: 'Recurring',
      start_date: moment(schedule.start_date),
      end_date: moment(schedule.end_date),
      isForever: !schedule.end_date,
      days,
    } as RecurringScheduleForm
  }

  return {
    reason_code: getInputOption(supplyOutage.reason_code),
    notes: supplyOutage.notes,
    schedule_local,
    terminal: getInputOption(supplyOutage.terminal_name, supplyOutage.terminal_id),
    product_group: getInputOption(supplyOutage.product_group),
    product: getInputOption(supplyOutage.product_name, supplyOutage.product_id),
    price_type: getInputOption(supplyOutage.price_type),
    supplier: getInputOption(supplyOutage.supplier_name, supplyOutage.supplier_id),
    contract: getInputOption(supplyOutage.contract),
    supply_owner: getInputOption(supplyOutage.supply_owner_name, supplyOutage.supply_owner_id),
  } as OutageForm
}

export function formatFormToOutage(formValues: OutageForm, outageId: string): Gravitate.SupplyOutage {
  const schedule = formValues.schedule_local
  const scheduleType = schedule.schedule_type

  let schedule_local: Gravitate.EventSchedule | Gravitate.RecurringSchedule
  if (scheduleType === 'Event') {
    schedule_local = {
      schedule_type: 'event',
      start_date: concatDateTime(schedule.start_date, schedule.start_time),
      end_date: schedule.isForever ? null : concatDateTime(schedule.end_date, schedule.end_time),
    }
  } else if (scheduleType === 'Recurring') {
    const days = Object.entries(schedule.days).reduce((acc, [key, value]) => {
      if (!value.enabled) return acc
      return [
        ...acc,
        {
          day_of_week: key,
          start_time: value.start_time.format('HH:mm'),
          end_time: value.end_time.format('HH:mm'),
        },
      ]
    }, [])
    schedule_local = {
      schedule_type: 'recurring',
      start_date: moment(schedule.start_date).format('YYYY-MM-DD'),
      end_date: schedule.isForever ? null : moment(schedule.end_date).format('YYYY-MM-DD'),
      days,
    }
  }
  return {
    id: outageId,
    reason_code: formValues.reason_code.label,
    notes: formValues.notes,
    schedule_local,
    terminal_id: formValues.terminal?.key,
    terminal_name: formValues.terminal?.label,
    product_group: formValues.product_group?.label,
    product_name: formValues.product?.label,
    product_id: formValues.product?.key,
    price_type: formValues.price_type?.label,
    supplier_name: formValues.supplier?.label,
    supplier_id: formValues.supplier?.key,
    contract: formValues.contract?.label,
    supply_owner_name: formValues.supply_owner?.label,
    supply_owner_id: formValues.supply_owner?.key,
  } as Gravitate.SupplyOutage
}

export function getInputOption(label: string, key?: string) {
  return {
    key: key || label,
    label,
    value: label,
  } as SelectInputOption
}

export function concatDateTime(date: Moment, time: Moment) {
  // Create moment in local timezone
  return `${date.format('YYYY-MM-DD')}T${time.format('HH:mm')}`
}

export function isStartEndInvalid(start: string, end: string): boolean {
  const moment1 = moment(start)
  const moment2 = moment(end)

  if (!moment1.isValid() || !moment2.isValid()) {
    return false
  }

  return moment1.isSame(moment2) || moment1.isAfter(moment2)
}

export function sortMetadataOptionsAlphabetically(
  a: string | OutageMetadataBaseItem,
  b: string | OutageMetadataBaseItem
) {
  const aValue = typeof a === 'string' ? a : a.name
  const bValue = typeof b === 'string' ? b : b.name
  return aValue.localeCompare(bValue)
}

export function formatSelectOptions(r: string | OutageMetadataBaseItem) {
  if (typeof r === 'string') {
    return { value: r, label: r }
  }
  return {
    value: r.id,
    label: r.name,
  }
}

export const metadataQuery = (market: string) => ({
  reason_code: {
    format: 'supply_outages',
    source: 'reason_codes',
  },
  terminal: {
    source: 'locations',
    type: 'Terminal',
    market: market?.length > 0 ? market : 'None',
    format: 'id_name',
  },
  product: {
    format: 'id_name',
    source: 'products',
  },
  supplier: {
    format: 'id_name',
    source: 'counterparties',
    type: 'Supplier',
  },
  product_group: {
    format: 'product_groups',
    source: 'products',
  },
  price_type: {
    format: 'price_types',
    source: 'prices',
  },
  contract: {
    format: 'contracts',
    source: 'prices',
  },
  supply_owner: {
    format: 'supply_owners',
    source: 'prices',
  },
})
